import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, ArrowRight, Users, Trash2 } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import DashShell from "@/components/dash/DashShell";
import { Card, StatusPill } from "@/components/portal/ui";
import NotificationBell from "@/components/dash/NotificationBell";
import { OP_LINKS } from "./OperatorDashboard";

const NAMES = { diagnostic: "Clarity", blueprint: "Blueprint", guided: "Guided Virtual Consulting", inhome_overnight: "In-Home + Overnight" };
const FILTERS = [["all", "All"], ["new", "New"], ["active", "Active"], ["awaiting_consultant", "Awaiting Consultant"], ["awaiting_client", "Awaiting Client"], ["completed", "Completed"]];

export default function OperatorClients() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  useSeo({ title: "Clients", noindex: true });

  const load = useCallback(() => {
    http.get(`/operator/clients?search=${encodeURIComponent(q)}&status=${status}`).then((r) => setRows(r.data.clients)).catch(() => setRows([]));
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const delTest = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Delete test account "${c.name || c.email}" and its test data? This cannot be undone.`)) return;
    try { await http.delete(`/admin/test-customer/${c.customer_id}`); toast.success("Test account deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <DashShell brandLabel="Consultant Workspace" links={OP_LINKS("clients")} rightSlot={<NotificationBell />}>
      <div className="mb-5">
        <div className="eyebrow text-[#8A998C] mb-1">Client management</div>
        <h1 className="font-display text-3xl text-[#1A1918]">Clients</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A998C]" />
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, child, or email…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#E5E1D8] bg-white text-sm text-[#1A1918] focus:border-[#9F634E] outline-none" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="client-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setStatus(k)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${status === k ? "bg-[#1A1918] text-white border-[#1A1918]" : "bg-white text-[#6B665E] border-[#E5E1D8] hover:border-[#9F634E]/40"}`}>{l}</button>
        ))}
      </div>

      {rows === null ? <p className="text-[#6B665E]">Loading…</p> : rows.length === 0 ? (
        <Card className="p-10 text-center" data-testid="clients-empty">
          <Users size={24} className="text-[#C9C3B8] mx-auto mb-3" />
          <h3 className="font-display text-lg text-[#1A1918] mb-1">No clients {status !== "all" ? "in this view" : "yet"}</h3>
          <p className="text-[#6B665E] text-sm">New customers appear here after their first successful purchase.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-x-auto" data-testid="clients-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4] text-xs uppercase tracking-wide">
              {["Parent", "Child", "Age", "Products", "Purchased", "Status", "Last activity", ""].map((h, i) => <th key={i} className="p-4 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.customer_id} data-testid={`client-row-${c.customer_id}`} onClick={() => nav(`/operator/customer/${c.customer_id}`)}
                  className="border-b border-[#F7F4EE] last:border-0 hover:bg-[#FAF8F3] cursor-pointer">
                  <td className="p-4 text-[#1A1918]">{c.name || "—"}{c.is_test && <span className="ml-2 text-[9px] uppercase bg-[#C49A5A]/20 text-[#8a6a2e] rounded-full px-1.5 py-0.5">Test</span>}<div className="text-[#8A998C] text-xs">{c.email}</div></td>
                  <td className="p-4 text-[#6B665E]">{c.child_name || "—"}</td>
                  <td className="p-4 text-[#6B665E]">{c.child_age != null ? `${c.child_age} mo` : "—"}</td>
                  <td className="p-4 text-[#6B665E]">{c.products.map((p) => NAMES[p] || p).join(", ")}</td>
                  <td className="p-4 text-[#8A998C]">{c.purchase_date ? new Date(c.purchase_date).toLocaleDateString() : "—"}</td>
                  <td className="p-4"><StatusPill state={c.status} /></td>
                  <td className="p-4 text-[#8A998C]">{c.last_activity ? new Date(c.last_activity).toLocaleDateString() : "—"}</td>
                  <td className="p-4">{c.is_test
                    ? <button onClick={(e) => delTest(e, c)} data-testid={`client-delete-${c.customer_id}`} title="Delete test account" className="inline-flex items-center gap-1 text-xs text-[#9E4F4F] hover:underline"><Trash2 size={13} /> Delete</button>
                    : <ArrowRight size={14} className="text-[#8A998C]" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </DashShell>
  );
}
